/**
 * Attaches a given access token to a Merchant Configuration API call. Returns merchant invite information
 * @param accessToken
 */
export async function getFromMCAPI(accessToken, endpoint) {
  const bearer = `Bearer ${accessToken}`;
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
    Pragma: "no-cache",
    Authorization: bearer,
  });

  const options = {
    method: "GET",
    headers: headers,
  };

  //  Deliberately throws rather than resolving to undefined. It used to swallow every
  //  failure and return undefined, which callers then fed straight into setState: the
  //  merchants page rendered "No merchant data available" for what was actually a 401, and
  //  the invite page white-screened on partners.map(undefined). A failure the user cannot
  //  see is worse than one they can.
  return fetch(endpoint, options).then(async (response) => {
    if (!response.ok) {
      const error = new Error(
        response.status === 401 || response.status === 403
          ? "Not authorized to read this data."
          : `Request failed (HTTP ${response.status}).`
      );
      error.status = response.status;
      throw error;
    }

    return response.json();
  });
}

/**
 * Update merchant data in the Merchant Configuration API
 * @param {string} accessToken - The access token for authorization
 * @param {string} endpoint - The API endpoint
 * @param {object} data - The merchant data to update
 */
export async function updateMerchantInMCAPI(accessToken, endpoint, data) {
  const bearer = `Bearer ${accessToken}`;
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
    Pragma: "no-cache",
    Authorization: bearer,
  });
  console.log("Updating merchant with data:", data);
  const options = {
    method: "PUT",
    headers: headers,
    body: JSON.stringify(data),
  };

  return fetch(endpoint, options)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .catch((error) => {
      console.error("Error updating merchant:", error);
      throw error;
    });
}

/**
 * Get IC++ (Network Cost Passthrough) status for a merchant
 * @param {string} accessToken - The access token for authorization
 * @param {string} baseUri - The base URI for the merchant configuration API
 * @param {string} merchantId - The merchant ID
 */
export async function getIcPlusPlusStatus(accessToken, baseUri, merchantId) {
  const bearer = `Bearer ${accessToken}`;
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
    Pragma: "no-cache",
    Authorization: bearer,
  });

  const options = {
    method: "GET",
    headers: headers,
  };

  return fetch(`${baseUri}/api/merchants/${merchantId}/ic-plus-plus`, options)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Failed to get IC++ status");
      }
      return response.json();
    })
    .catch((error) => {
      console.error("Error getting IC++ status:", error);
      throw error;
    });
}

/**
 * Update IC++ (Network Cost Passthrough) status for a merchant
 * @param {string} accessToken - The access token for authorization
 * @param {string} baseUri - The base URI for the merchant configuration API
 * @param {string} merchantId - The merchant ID
 * @param {boolean} enabled - Whether IC++ should be enabled or disabled
 */
export async function updateIcPlusPlusStatus(accessToken, baseUri, merchantId, enabled) {
  const bearer = `Bearer ${accessToken}`;
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
    Pragma: "no-cache",
    Authorization: bearer,
  });

  const options = {
    method: "PUT",
    headers: headers,
    body: JSON.stringify({ enabled: enabled }),
  };

  return fetch(`${baseUri}/api/merchants/${merchantId}/ic-plus-plus`, options)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Failed to update IC++ status");
      }
      return response.json();
    })
    .catch((error) => {
      console.error("Error updating IC++ status:", error);
      throw error;
    });
}

/**
 * Get a merchant's payout start of day, as Stripe has it.
 * @param {string} accessToken - The access token for authorization
 * @param {string} baseUri - The base URI for the merchant configuration API
 * @param {string} merchantId - The merchant ID
 * @returns {Promise<object>} { merchantId, paymentProcessorAccountId, timezone, hour, minutes }
 */
export async function getStartOfDay(accessToken, baseUri, merchantId) {
  const bearer = `Bearer ${accessToken}`;
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
    Pragma: "no-cache",
    Authorization: bearer,
  });

  const options = {
    method: "GET",
    headers: headers,
  };

  return fetch(`${baseUri}/api/merchants/${merchantId}/start-of-day`, options)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Failed to get start of day");
      }
      return response.json();
    })
    .catch((error) => {
      console.error("Error getting start of day:", error);
      throw error;
    });
}

/**
 * Set a merchant's payout start of day.
 *
 * Like deleteMerchantInMCAPI, this keeps the server's message. A 400 here is usually
 * Stripe refusing the value, and its message says which timezones or hours are allowed.
 *
 * @param {string} accessToken - The access token for authorization
 * @param {string} baseUri - The base URI for the merchant configuration API
 * @param {string} merchantId - The merchant ID
 * @param {{timezone: string, hour: number, minutes: number}} startOfDay - The new start of day
 * @throws {Error} with `status` set and the server's message on failure
 */
export async function updateStartOfDay(accessToken, baseUri, merchantId, startOfDay) {
  const bearer = `Bearer ${accessToken}`;
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
    Pragma: "no-cache",
    Authorization: bearer,
  });

  const options = {
    method: "PUT",
    headers: headers,
    body: JSON.stringify({
      timezone: startOfDay.timezone,
      hour: startOfDay.hour,
      minutes: startOfDay.minutes,
    }),
  };

  const response = await fetch(
    `${baseUri}/api/merchants/${merchantId}/start-of-day`,
    options
  );

  if (response.ok) return response.json();

  //  The API answers BadRequest("...") with the message as a JSON string. 403 and some
  //  500s have no body at all.
  let message = null;
  try {
    const text = await response.text();
    try {
      const body = JSON.parse(text);
      message = typeof body === "string" ? body : body?.message || body?.title;
    } catch {
      message = text;
    }
  } catch {
    // Fall through to the status.
  }

  const error = new Error(
    message || `Failed to update start of day (HTTP ${response.status})`
  );
  error.status = response.status;

  console.error("Error updating start of day:", error);
  throw error;
}

/**
 * Check whether a merchant can be hard deleted, without deleting anything.
 * Read-only dry run of the API's pre-delete Stripe guard.
 * @param {string} accessToken - The access token for authorization
 * @param {string} baseUri - The base URI for the merchant configuration API
 * @param {string} merchantId - The merchant ID
 * @returns {Promise<object>} The guard's verdict: { safe_to_delete, reason, message, balances, ... }
 */
export async function getMerchantDeletability(accessToken, baseUri, merchantId) {
  const bearer = `Bearer ${accessToken}`;
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
    Pragma: "no-cache",
    Authorization: bearer,
  });

  const options = {
    method: "GET",
    headers: headers,
  };

  return fetch(`${baseUri}/api/merchants/${merchantId}/deletability`, options)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Failed to check whether the merchant can be deleted");
      }
      return response.json();
    })
    .catch((error) => {
      console.error("Error checking merchant deletability:", error);
      throw error;
    });
}

/**
 * Permanently delete a merchant, its invites, and its Stripe connected account.
 * None of this is reversible.
 *
 * Unlike the other wrappers in this file, this one does NOT discard the server's
 * message. A 409 means the API's guard refused the delete and the body says why
 * (funds on hand, an open payout, recent activity); showing the operator a generic
 * "something went wrong" instead would waste the only useful part of the response.
 *
 * @param {string} accessToken - The access token for authorization
 * @param {string} baseUri - The base URI for the merchant configuration API
 * @param {string} merchantId - The merchant ID
 * @throws {Error} with `status` and `details` set from the response body on failure
 */
export async function deleteMerchantInMCAPI(accessToken, baseUri, merchantId) {
  const bearer = `Bearer ${accessToken}`;
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
    Pragma: "no-cache",
    Authorization: bearer,
  });

  const options = {
    method: "DELETE",
    headers: headers,
  };

  const response = await fetch(
    `${baseUri}/api/merchants/${merchantId}`,
    options
  );

  // Success is 204 No Content, so there is no body to parse.
  if (response.ok) return true;

  let details = null;
  try {
    details = await response.json();
  } catch {
    // 403 and some 500s come back without a JSON body. Fall through to the status.
  }

  const error = new Error(
    details?.message || `Failed to delete merchant (HTTP ${response.status})`
  );
  error.status = response.status;
  error.details = details;

  console.error("Error deleting merchant:", error);
  throw error;
}

/**
 * Starts building the TowPay admin merchant report, or returns the run already in progress.
 * The API builds it in the background; poll getLatestMerchantReport until it finishes.
 *
 * @param {string} accessToken - The access token for authorization
 * @param {string} baseUri - The base URI for the merchant configuration API
 * @returns {Promise<object>} the run: { id, status, requestedBy, startedUtc, total, processed, ... }
 * @throws {Error} with `status` set on failure
 */
export async function startMerchantReport(accessToken, baseUri) {
  const response = await fetch(`${baseUri}/api/merchant-reports`, {
    method: "POST",
    headers: new Headers({
      Accept: "application/json",
      Pragma: "no-cache",
      Authorization: `Bearer ${accessToken}`,
    }),
  });

  if (response.ok) return response.json();

  throw merchantReportError(response, "Failed to start the merchant report");
}

/**
 * The newest merchant report run, with its rows once it has completed.
 *
 * @param {string} accessToken - The access token for authorization
 * @param {string} baseUri - The base URI for the merchant configuration API
 * @returns {Promise<object|null>} the run, or null when no report has ever been generated
 * @throws {Error} with `status` set on failure
 */
export async function getLatestMerchantReport(accessToken, baseUri) {
  const response = await fetch(`${baseUri}/api/merchant-reports/latest`, {
    method: "GET",
    headers: new Headers({
      Accept: "application/json",
      Pragma: "no-cache",
      Authorization: `Bearer ${accessToken}`,
    }),
  });

  //  404 is the normal answer before the first report, not a failure.
  if (response.status === 404) return null;
  if (response.ok) return response.json();

  throw merchantReportError(response, "Failed to load the merchant report");
}

function merchantReportError(response, message) {
  const error = new Error(
    response.status === 401 || response.status === 403
      ? "Not authorized to use the merchant report."
      : `${message} (HTTP ${response.status}).`
  );
  error.status = response.status;
  return error;
}
